import { useMemo, useRef, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'

import { useSites } from 'store'

import { useProjectsFilters, useProjectsSort } from './store'

const OPERATION_KPIS = gql`
  query projectsCounter($sfIds: [ID]!) {
    counter: projectsCounter(site_sf_ids: $sfIds) {
      key
      count
    }
  }
`

const OPERATION_PROCESS = gql`
  query findProjectProcess($id: ID!) {
    process: projectProcess(id: $id) {
      id
      kind
      status
      services {
        name
        category
      }
      start_at
      end_at
      private_start_at
      document {
        id
        type
        name
      }
    }
  }
`
const OPERATION_PROJECT = gql`
  query findProject($id: ID!) {
    project: project(id: $id) {
      id
      created_at
      mes_at
      state
      site {
        name
        full_address
        location {
          longitude
          latitude
        }
      }
      manager {
        email
        name
      }
      services {
        kind
        commissioned_at
        state
        name
        category
        created_at
      }
      contacts {
        email
        salutation
        title
        first_name
        last_name
        phone
        mobile_phone
      }
      documents {
        id
        step
        name
        type
        created_at
        updated_at
      }
    }
  }
`
const OPERATION_PROJECTS = gql`
  query findProjects($sfIds: [ID], $state: String, $services: [String], $offset: Int, $limit: Int, $sort_by: String, $sort_direction: String) {
    projects: projects(site_sf_ids: $sfIds, state: $state, services: $services, offset: $offset, limit: $limit, sort_by: $sort_by, sort_direction: $sort_direction) {
      id
      created_at
      state
      site {
        name
        full_address
      }
      manager {
        email
        name
      }
      services {
        kind
        commissioned_at
        state
        name
        created_at
      }
    }
  }
`

export interface IProcessService {
  category: string
  name: string
}

export interface IProcess {
  id: string
  start_at: string
  end_at: string
  private_start_at?: string
  status: string
  kind: string
  services: IProcessService[]
}

const PAGE_SIZE = 25

export const useKpis = () => {
  const [{ selected: sitesSelected }] = useSites()
  const sfIds: string[] = useMemo(
    () => (sitesSelected ? sitesSelected.map(({ sf_id }) => sf_id as string) : []),
    [sitesSelected],
  )
  const { data, loading, error } = useQuery(OPERATION_KPIS, {
    variables: {
      sfIds
    }
  })
  return {
    data: data?.counter,
    loading,
    error
  }
}

export const useProjectProcess = () => {
  const {id} = useParams()
  const { data, loading, error } = useQuery(OPERATION_PROCESS, {
    variables: {
      id
    }
  })
  return {
    data: data?.process,
    loading,
    error
  }
}
export const useProject = () => {
  const {id} = useParams()
  const { data, loading, error } = useQuery(OPERATION_PROJECT, {
    variables: {
      id
    }
  })
  return {
    data: data?.project,
    loading,
    error
  }
}
const useProjects = () => {
  const page = useRef<number>(1)
  const [shouldFetchNextPage, setShouldFetchNextPage] = useState<boolean>(true)
  const [{ selected: sitesSelected }] = useSites()
  const sfIds: string[] = useMemo(
    () => (sitesSelected ? sitesSelected.map(({ sf_id }) => sf_id as string) : []),
    [sitesSelected],
  )
  const [filters] = useProjectsFilters()
  const [sort] = useProjectsSort()
  const {loading, data, error, fetchMore, networkStatus} = useQuery(OPERATION_PROJECTS, {
    variables: {
      sfIds,
      sort_by: sort.by,
      sort_direction: sort.direction,
      state: filters.state,
      services: filters.services,
      limit: PAGE_SIZE,
    },
    notifyOnNetworkStatusChange: true
  })
  const projects = useMemo(() => (!error && data ? data.projects : []), [error, data])

  return {
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage: !loading && projects.length >= PAGE_SIZE && shouldFetchNextPage,
    fetchNextPage: async () => {
      if (networkStatus === 3) {
        return
      }
      page.current++
      const fetchMoreResult = await fetchMore({
        variables: {
          offset: projects.length,
          limit: PAGE_SIZE
        }
      })

      if (!fetchMoreResult.data?.projects?.length || fetchMoreResult.data?.projects?.length < PAGE_SIZE) {
        setShouldFetchNextPage(false)
      }
    },
    projects: projects.filter(({state, services}) => !['closed','vsr'].includes(state) || services.length > 0),
    error
  }
}

export default useProjects